<template>
  <div class="mz-root">

    <!-- Splash -->
    <transition name="splash-out">
      <div v-if="showSplash" class="mz-splash">
        <div class="splash-inner">
          <div class="splash-mark">
            <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
              <rect width="48" height="48" rx="14" fill="#d48c1c"/>
              <path d="M12 34V18l8 10 4-6 4 6 8-10v16" stroke="#0c0a0e" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" fill="none"/>
            </svg>
          </div>
          <h1 class="splash-wordmark">Muzilly</h1>
          <p class="splash-line">Listen free. Belong for real.</p>
          <div class="splash-bar"><div class="splash-progress"></div></div>
        </div>
      </div>
    </transition>

    <!-- Main -->
    <transition name="page-in">
      <div v-if="!showSplash" class="mz-page">
      <div class="mz-layout">

        <!-- Left: what Muzilly is -->
        <section class="mz-brand-panel" aria-label="About Muzilly">
          <div class="brand-glow" aria-hidden="true"></div>
          <div class="brand-panel-inner">
            <div class="brand-mark">
              <svg width="36" height="36" viewBox="0 0 48 48" fill="none" aria-hidden="true">
                <rect width="48" height="48" rx="14" fill="#d48c1c"/>
                <path d="M12 34V18l8 10 4-6 4 6 8-10v16" stroke="#0c0a0e" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" fill="none"/>
              </svg>
              <span class="brand-wordmark">Muzilly</span>
            </div>

            <h2 class="manifesto-headline">Music, music videos and podcasts, paid for by the people who listen.</h2>
            <p class="manifesto-body">Members fund the artists they follow, and artists are paid from a monthly pool by listening share. No ads, no middlemen.</p>

            <!-- Three formats, one app. The playing card is the one moving thing on the page. -->
            <div class="formats" aria-hidden="true">
              <div class="fmt fmt-pod">
                <div class="fmt-art fmt-art-pod"><v-icon dark>mdi-podcast</v-icon></div>
                <div class="fmt-text"><b>Late Night Sessions</b><span>Podcast &middot; New episode</span></div>
              </div>
              <div class="fmt fmt-vid">
                <div class="fmt-art fmt-art-vid"><v-icon dark>mdi-play</v-icon></div>
                <div class="fmt-text"><b>Golden Hour (Official Video)</b><span>Music video &middot; First on Muzilly</span></div>
              </div>
              <div class="fmt fmt-trk">
                <div class="fmt-art fmt-art-trk">
                  <span class="eq"><i></i><i></i><i></i><i></i></span>
                </div>
                <div class="fmt-text"><b>Slow Burn</b><span>Track &middot; Playing now</span></div>
              </div>
            </div>

            <ul class="feature-list">
              <li><v-icon small color="#d48c1c">mdi-music-note</v-icon><span><b>Music</b> Stream tracks and albums, free to listen.</span></li>
              <li><v-icon small color="#d48c1c">mdi-video-outline</v-icon><span><b>Music videos</b> Watch premieres and first-on-Muzilly drops.</span></li>
              <li><v-icon small color="#d48c1c">mdi-podcast</v-icon><span><b>Podcasts</b> Follow shows and series from independent creators.</span></li>
              <li><v-icon small color="#d48c1c">mdi-access-point</v-icon><span><b>Live rooms</b> Join artists live, on camera or audio.</span></li>
            </ul>

            <div class="get-app">
              <div class="get-app-title">Take Muzilly with you</div>
              <div class="store-row">
                <a class="store" :href="appStoreUrl" target="_blank" rel="noopener" aria-label="Download Muzilly on the App Store">
                  <v-icon color="#f5f0e8" size="26">mdi-apple</v-icon>
                  <span><small>Download on the</small><b>App Store</b></span>
                </a>
                <a class="store" :href="playStoreUrl" target="_blank" rel="noopener" aria-label="Get Muzilly on Google Play">
                  <v-icon color="#f5f0e8" size="26">mdi-google-play</v-icon>
                  <span><small>Get it on</small><b>Google Play</b></span>
                </a>
              </div>
            </div>
          </div>
        </section>

        <!-- Right: sign in -->
        <section class="mz-form-panel">
          <div class="form-panel-inner">

            <div class="mobile-brand" aria-hidden="true">
              <svg width="30" height="30" viewBox="0 0 48 48" fill="none">
                <rect width="48" height="48" rx="14" fill="#d48c1c"/>
                <path d="M12 34V18l8 10 4-6 4 6 8-10v16" stroke="#0c0a0e" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" fill="none"/>
              </svg>
              <span>Muzilly</span>
            </div>

            <div class="form-header">
              <h3 class="form-title">Welcome back</h3>
              <p class="form-sub">Sign in to keep listening, watching and following.</p>
            </div>

            <div v-if="fromShare" class="share-note">
              <v-icon small color="#d48c1c">mdi-link-variant</v-icon>
              <span>Sign in and we'll take you to the link you opened.</span>
            </div>

            <v-form ref="loginForm" v-model="formValid" lazy-validation @submit.prevent="handleSubmit">

              <div class="field-group">
                <label class="field-label" for="mz-email">Email address</label>
                <v-text-field
                  id="mz-email"
                  v-model="email"
                  type="email"
                  outlined
                  dense
                  placeholder="you@example.com"
                  prepend-inner-icon="mdi-email-outline"
                  :rules="emailRules"
                  :error-messages="emailErrorMsg"
                  autocomplete="email"
                  autocapitalize="off"
                  class="mz-input"
                  validate-on-blur
                  @focus="clearError('email')"
                  @blur="validateEmail"
                />
              </div>

              <div class="field-group">
                <label class="field-label" for="mz-password">Password</label>
                <v-text-field
                  id="mz-password"
                  v-model="password"
                  :type="showPassword ? 'text' : 'password'"
                  outlined
                  dense
                  placeholder="Enter your password"
                  prepend-inner-icon="mdi-lock-outline"
                  :append-icon="showPassword ? 'mdi-eye-off-outline' : 'mdi-eye-outline'"
                  :rules="passwordRules"
                  :error-messages="passwordErrorMsg"
                  autocomplete="current-password"
                  class="mz-input"
                  validate-on-blur
                  @click:append="showPassword = !showPassword"
                  @focus="clearError('password')"
                  @keyup.enter="handleSubmit"
                />
              </div>

              <div class="options-row">
                <v-checkbox v-model="rememberMe" color="#d48c1c" hide-details class="mz-checkbox">
                  <template #label><span class="checkbox-label">Remember me</span></template>
                </v-checkbox>
                <button type="button" class="forgot-link" @click="handleForgotPassword">Forgot password?</button>
              </div>

              <button
                type="submit"
                class="mz-submit"
                :class="{ loading: signInProcessing, disabled: !canSubmit }"
                :disabled="!canSubmit"
                @click.prevent="handleSubmit"
              >
                <span v-if="!signInProcessing">Sign in</span>
                <span v-else class="submit-loading"><span class="dot"></span><span class="dot"></span><span class="dot"></span></span>
              </button>

              <transition name="alert-in">
                <div v-if="signInErrorMessage" class="mz-alert error" role="alert">
                  <svg width="14" height="14" viewBox="0 0 16 16" fill="none" style="flex-shrink:0">
                    <circle cx="8" cy="8" r="6.5" stroke="#c0392b" stroke-width="1.2"/>
                    <path d="M8 5v4M8 11v.5" stroke="#c0392b" stroke-width="1.5" stroke-linecap="round"/>
                  </svg>
                  <span>{{ signInErrorMessage }}<template v-if="accountDisabled"> <router-link :to="{ path: '/appeal', query: { email: email } }" style="color:inherit;font-weight:600;text-decoration:underline">Appeal this decision</router-link></template></span>
                </div>
              </transition>

              <transition name="alert-in">
                <div v-if="infoMessage" class="mz-alert success" role="status">
                  <svg width="14" height="14" viewBox="0 0 16 16" fill="none" style="flex-shrink:0">
                    <circle cx="8" cy="8" r="6.5" stroke="#1D9E75" stroke-width="1.2"/>
                    <path d="M5.5 8l2 2 3-3" stroke="#1D9E75" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                  <span>{{ infoMessage }}</span>
                </div>
              </transition>

            </v-form>

            <div class="form-divider"><span>New to Muzilly?</span></div>

            <button class="mz-register-btn" @click="navigateToRegister">
              Create an account
              <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                <path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </button>

            <p class="register-note">Join as a fan, artist, producer, band or podcaster. You're in right away.</p>

            <!-- Phones and tablets don't show the left panel, so the apps and formats live here too -->
            <div class="mobile-promo">
              <div class="mobile-formats">
                <span><v-icon x-small color="#d48c1c">mdi-music-note</v-icon> Music</span>
                <span><v-icon x-small color="#d48c1c">mdi-video-outline</v-icon> Music videos</span>
                <span><v-icon x-small color="#d48c1c">mdi-podcast</v-icon> Podcasts</span>
                <span><v-icon x-small color="#d48c1c">mdi-access-point</v-icon> Live rooms</span>
              </div>
              <div class="store-row">
                <a class="store" :href="appStoreUrl" target="_blank" rel="noopener" aria-label="Download Muzilly on the App Store">
                  <v-icon color="#f5f0e8" size="24">mdi-apple</v-icon>
                  <span><small>Download on the</small><b>App Store</b></span>
                </a>
                <a class="store" :href="playStoreUrl" target="_blank" rel="noopener" aria-label="Get Muzilly on Google Play">
                  <v-icon color="#f5f0e8" size="24">mdi-google-play</v-icon>
                  <span><small>Get it on</small><b>Google Play</b></span>
                </a>
              </div>
            </div>

          </div>
        </section>

      </div>

      <!-- From the blog (hidden until there is at least one published post) -->
      <section v-if="blogPosts.length" class="mz-blog" aria-label="From the Muzilly blog">
        <div class="mz-blog-in">
          <div class="mz-blog-head">
            <h2>From the blog</h2>
            <router-link to="/blog" class="mz-blog-all">Read all posts</router-link>
          </div>
          <div class="mz-blog-grid">
            <router-link v-for="p in blogPosts" :key="p.id" :to="`/blog/${p.slug}`" class="mz-post">
              <div class="mz-post-img" :style="p.coverUrl ? { backgroundImage: `url(${p.coverUrl})` } : { background: postGradient(p.title) }">
                <span v-if="!p.coverUrl" aria-hidden="true">{{ (p.title || "M")[0] }}</span>
              </div>
              <div class="mz-post-meta"><b v-if="p.category">{{ p.category }}</b>{{ p.readMinutes || 1 }} min read</div>
              <h3>{{ p.title }}</h3>
              <p>{{ p.excerpt }}</p>
            </router-link>
          </div>
        </div>
      </section>

      <footer class="mz-foot">
        <span>© {{ new Date().getFullYear() }} Muzilly</span>
        <span class="mz-foot-links">
          <router-link to="/blog">Blog</router-link><router-link to="/about">About</router-link><router-link to="/faq">FAQ</router-link>
          <router-link to="/privacy">Privacy</router-link><router-link to="/terms">Terms</router-link>
        </span>
      </footer>
      </div>
    </transition>
  </div>
</template>

<script>
import {
  signInWithEmailAndPassword,
  setPersistence,
  browserLocalPersistence,
  browserSessionPersistence,
  sendPasswordResetEmail,
} from "firebase/auth";
import { auth, db } from "@/firebase";
import { listPublished } from "@/services/blogService";
import { doc, getDoc, setDoc, serverTimestamp } from "firebase/firestore";

// Store links. Play Store uses the package name. For the App Store paste your link
// (App Store Connect → App Information → Apple ID, then https://apps.apple.com/app/id<AppleID>).
const PLAY_STORE_URL = "https://play.google.com/store/apps/details?id=com.muzilly.app";
const APP_STORE_URL = "";
const SPLASH_DURATION = 1600;
const EMAIL_KEY = "muzilly_email";

export default {
  name: "LoginView",

  data() {
    return {
      email: "",
      password: "",
      rememberMe: true,
      showPassword: false,
      formValid: false,
      blogPosts: [],
      playStoreUrl: PLAY_STORE_URL,
      appStoreUrl: APP_STORE_URL || "https://apps.apple.com/us/search?term=Muzilly",
      showSplash: true,
      signInProcessing: false,
      signInErrorMessage: "",
      accountDisabled: false,
      infoMessage: "",
      emailErrorMsg: "",
      passwordErrorMsg: "",
      emailRules: [
        v => !!v || "Email is required",
        v => /.+@.+\..+/.test(v) || "Enter a valid email address",
      ],
      passwordRules: [
        v => !!v || "Password is required",
        v => (v && v.length >= 6) || "At least 6 characters",
      ],
    };
  },

  computed: {
    /** True when someone arrives from a shared link and will be sent there after signing in. */
    fromShare() {
      const n = this.$route && this.$route.query && this.$route.query.next;
      return typeof n === "string" && n.startsWith("/") && !n.startsWith("//");
    },
    registerTarget() {
      return this.fromShare ? { path: "/register", query: { next: this.$route.query.next } } : "/register";
    },
    canSubmit() {
      return (
        !this.signInProcessing &&
        this.email.length > 0 &&
        this.password.length >= 6
      );
    },
  },

  mounted() {
    try {
      const saved = localStorage.getItem(EMAIL_KEY);
      if (saved) this.email = saved;
    } catch {}
    // Latest blog posts for the section under the form. Fails quietly: the page works without them.
    listPublished(3).then((p) => { this.blogPosts = p; }).catch(() => {});
    setTimeout(() => {
      this.showSplash = false;
      this.$nextTick(() => {
        this.$refs.loginForm?.$el?.querySelector("input")?.focus();
      });
    }, SPLASH_DURATION);
  },

  methods: {
    postGradient(title) {
      const pairs = [["#3a2410", "#d48c1c"], ["#2a1d47", "#8a5bd6"], ["#3d1a1a", "#d4572a"], ["#10302a", "#2fa88a"], ["#1d2a47", "#4f86d6"]];
      let h = 0; for (const c of String(title || "")) h = (h * 31 + c.charCodeAt(0)) >>> 0;
      const [a, b] = pairs[h % pairs.length];
      return `linear-gradient(135deg, ${a} 0%, ${a} 35%, ${b} 130%)`;
    },

    clearError(field) {
      if (field === "email") this.emailErrorMsg = "";
      if (field === "password") this.passwordErrorMsg = "";
      this.signInErrorMessage = "";
      this.accountDisabled = false;
    },

    validateEmail() {
      if (!this.email) return (this.emailErrorMsg = "Email is required");
      if (!/.+@.+\..+/.test(this.email)) return (this.emailErrorMsg = "Enter a valid email address");
      this.emailErrorMsg = "";
    },

    async handleSubmit() {
      if (!this.canSubmit) return;
      if (!this.$refs.loginForm.validate()) return;
      await this.signInUser();
    },

    async signInUser() {
      this.signInProcessing = true;
      this.signInErrorMessage = "";
      this.accountDisabled = false;
      this.infoMessage = "";
      try {
        await setPersistence(auth, this.rememberMe ? browserLocalPersistence : browserSessionPersistence);
        const { user } = await signInWithEmailAndPassword(auth, this.email.trim(), this.password);
        try {
          if (this.rememberMe) localStorage.setItem(EMAIL_KEY, this.email);
          else localStorage.removeItem(EMAIL_KEY);
        } catch {}
        await this.routeUser(user);
      } catch (err) {
        this.accountDisabled = err.code === "auth/user-disabled";
        this.signInErrorMessage = this.errorMessage(err.code);
      } finally {
        this.signInProcessing = false;
      }
    },

    /** Where a shared link wants us to go after sign-in. Same-site paths only. */
    nextRoute() {
      const n = this.$route && this.$route.query && this.$route.query.next;
      return typeof n === "string" && n.startsWith("/") && !n.startsWith("//") ? n : "";
    },

    async routeUser(user) {
      const snap = await getDoc(doc(db, "user", user.uid));
      if (snap.exists()) {
        const { type, role, muzillyRole } = snap.data();
        const userType = type || role;
        const routes = {
          Minister: "/minister-home",
          Admin: "/admin-dashboard",
          Artist: "/artist-home",
          Rising: "/rising-home",
          Producer: "/artist-home",
          Band: "/artist-home",
          Label: "/label-home",
          Fan: "/fan-home",
          // Kept for any accounts created before the type rework.
          Member: "/fan-home",
        };
        let route = routes[userType];
        // Podcasters are fan accounts that land on their Studio.
        if (route === "/fan-home" && String(muzillyRole || "").toLowerCase() === "podcaster") route = "/podcast-studio";
        // Came from a shared link (?next=/artist/…): land on what was shared.
        if (route) this.$router.push(this.nextRoute() || route);
        else this.signInErrorMessage = "Account type not recognised. Contact support.";
      } else {
        // Legacy path: an account created before sign-up wrote the user record
        // directly. Its details live in the old pre-launch collection; promote
        // it to a full user record now so it can sign straight in.
        const wl = await getDoc(doc(db, "waitlist", user.uid));
        if (wl.exists()) {
          const d = wl.data() || {};
          const roleTypeMap = { artist: "Artist", rising: "Rising", producer: "Producer", band: "Band", label: "Label", fan: "Fan", podcaster: "Fan" };
          const resolvedType = roleTypeMap[d.muzillyRole] || "Fan";
          await setDoc(doc(db, "user", user.uid), {
            uid: user.uid,
            fullName: d.fullName || user.displayName || "",
            email: d.email || user.email || "",
            image: d.image || null,
            username: d.username || null,
            genres: d.genres || [],
            location: d.location || null,
            role: resolvedType,
            type: resolvedType,
            createdAt: serverTimestamp(),
          }, { merge: true });
          const destinationMap = {
            Artist: "/artist-home", Rising: "/rising-home", Producer: "/artist-home",
            Band: "/artist-home", Label: "/label-home", Fan: "/fan-home",
          };
          this.$router.push(this.nextRoute() || destinationMap[resolvedType] || "/fan-home");
        } else {
          this.$router.push("/register");
        }
      }
    },

    errorMessage(code) {
      return ({
        "auth/user-not-found": "No account found with this email.",
        "auth/wrong-password": "Incorrect password.",
        "auth/invalid-email": "Enter a valid email address.",
        "auth/user-disabled": "This account has been disabled. Contact support.",
        "auth/too-many-requests": "Too many attempts. Try again later.",
        "auth/network-request-failed": "Network error. Check your connection.",
        "auth/invalid-credential": "Email or password is incorrect.",
      })[code] || "Unable to sign in. Please try again.";
    },

    async handleForgotPassword() {
      this.signInErrorMessage = "";
      this.accountDisabled = false;
      this.infoMessage = "";
      if (!this.email) {
        this.signInErrorMessage = "Enter your email address first.";
        return;
      }
      if (!/.+@.+\..+/.test(this.email)) {
        this.signInErrorMessage = "Enter a valid email address.";
        return;
      }
      try {
        await sendPasswordResetEmail(auth, this.email.trim());
        this.infoMessage = "Reset email sent — check your inbox.";
      } catch (err) {
        this.signInErrorMessage = this.errorMessage(err.code);
      }
    },

    navigateToRegister() {
      this.$router.push(this.registerTarget);
    },
  },
};
</script>

<style scoped>
/* ─── Root ─────────────────────────────────────────── */
.mz-root {
  min-height: 100vh;
  background: #0c0a0e;
  font-family: 'DM Sans', 'Helvetica Neue', sans-serif;
}

/* ─── Splash ────────────────────────────────────────── */
.mz-splash {
  position: fixed;
  inset: 0;
  background: #0c0a0e;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}
.splash-inner {
  text-align: center;
  animation: splashFadeIn 0.5s ease both;
}
@keyframes splashFadeIn {
  from { opacity: 0; transform: translateY(12px); }
  to   { opacity: 1; transform: translateY(0); }
}
.splash-mark {
  margin-bottom: 20px;
}
.splash-wordmark {
  font-size: 32px;
  font-weight: 500;
  color: #f5f0e8;
  letter-spacing: -0.5px;
  margin: 0 0 8px;
}
.splash-line {
  font-size: 14px;
  color: rgba(245,240,232,0.4);
  letter-spacing: 0.3px;
  margin: 0 0 32px;
}
.splash-bar {
  width: 120px;
  height: 2px;
  background: rgba(212,140,28,0.15);
  border-radius: 2px;
  margin: 0 auto;
  overflow: hidden;
}
.splash-progress {
  height: 100%;
  background: #d48c1c;
  border-radius: 2px;
  animation: loadBar 1.4s cubic-bezier(0.4,0,0.2,1) forwards;
}
@keyframes loadBar {
  from { width: 0%; }
  to   { width: 100%; }
}

.splash-out-leave-active {
  transition: opacity 0.4s ease;
}
.splash-out-leave-to {
  opacity: 0;
}

/* ─── Layout ────────────────────────────────────────── */
.mz-layout { min-height: 100vh; display: grid; grid-template-columns: minmax(0, 1.15fr) minmax(0, 1fr); }
.page-in-enter-active { transition: opacity 0.5s ease 0.1s; }
.page-in-enter { opacity: 0; }

/* ─── Left panel ────────────────────────────────────── */
.mz-brand-panel {
  position: relative; overflow: hidden; background: #100e13;
  border-right: 0.5px solid rgba(212,140,28,0.12);
  display: flex; align-items: center; justify-content: center; padding: 56px 56px; min-height: 100vh;
}
.brand-glow {
  position: absolute; inset: auto -20% -35% -20%; height: 70%;
  background: radial-gradient(60% 60% at 50% 100%, rgba(212,140,28,0.22), rgba(212,140,28,0) 70%);
  pointer-events: none;
}
.brand-panel-inner { position: relative; max-width: 460px; width: 100%; }
.brand-mark { display: flex; align-items: center; gap: 12px; margin-bottom: 44px; }
.brand-wordmark { font-size: 21px; font-weight: 500; color: #f5f0e8; letter-spacing: -0.3px; }
.manifesto-headline {
  font-family: 'Iowan Old Style', 'Palatino Linotype', Palatino, Georgia, serif;
  font-size: 36px; font-weight: 600; color: #f5f0e8; line-height: 1.16; letter-spacing: -0.6px; margin: 0 0 16px;
}
.manifesto-body { font-size: 15px; color: rgba(245,240,232,0.6); line-height: 1.65; margin: 0 0 34px; max-width: 420px; }

/* the three formats, fanned */
.formats { position: relative; height: 176px; margin: 0 0 30px; }
.fmt {
  position: absolute; left: 0; right: 0; display: flex; align-items: center; gap: 14px; padding: 12px 14px;
  background: #1a171e; border: 0.5px solid rgba(245,240,232,0.1); border-radius: 14px;
  box-shadow: 0 14px 30px rgba(0,0,0,0.45);
}
.fmt-pod { top: 0; transform: translateX(-10px) scale(0.94); opacity: 0.55; }
.fmt-vid { top: 52px; transform: translateX(10px) scale(0.97); opacity: 0.8; }
.fmt-trk { top: 104px; border-color: rgba(212,140,28,0.45); }
.fmt-art { width: 56px; height: 56px; border-radius: 10px; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
.fmt-art-pod { background: linear-gradient(135deg, #5b3a8c, #2a1d47); }
.fmt-art-vid { background: linear-gradient(135deg, #8c3a3a, #3d1a1a); }
.fmt-art-trk { background: linear-gradient(135deg, #d48c1c, #7a4f0d); }
.fmt-text { display: flex; flex-direction: column; min-width: 0; }
.fmt-text b { font-size: 14px; font-weight: 600; color: #f5f0e8; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.fmt-text span { font-size: 12px; color: rgba(245,240,232,0.5); margin-top: 2px; }
.eq { display: flex; align-items: flex-end; gap: 3px; height: 22px; }
.eq i { display: block; width: 4px; background: #0c0a0e; border-radius: 2px; animation: eqBar 1s ease-in-out infinite; }
.eq i:nth-child(1) { height: 10px; animation-delay: -0.2s; }
.eq i:nth-child(2) { height: 20px; animation-delay: -0.5s; }
.eq i:nth-child(3) { height: 14px; animation-delay: -0.8s; }
.eq i:nth-child(4) { height: 18px; animation-delay: -0.35s; }
@keyframes eqBar { 0%, 100% { transform: scaleY(0.35); } 50% { transform: scaleY(1); } }
.eq i { transform-origin: bottom; }

.feature-list { list-style: none; margin: 0 0 34px; padding: 0; display: flex; flex-direction: column; gap: 12px; }
.feature-list li { display: flex; align-items: flex-start; gap: 12px; font-size: 13.5px; color: rgba(245,240,232,0.55); line-height: 1.45; }
.feature-list b { color: #f5f0e8; font-weight: 500; margin-right: 4px; }
.feature-list .v-icon { margin-top: 1px; }

.get-app-title { font-size: 13px; color: rgba(245,240,232,0.5); margin-bottom: 10px; }
.store-row { display: flex; flex-wrap: wrap; gap: 10px; }
.store {
  display: inline-flex; align-items: center; gap: 10px; padding: 8px 16px 8px 12px; text-decoration: none;
  background: #000; border: 1px solid rgba(245,240,232,0.28); border-radius: 10px; color: #f5f0e8;
  transition: border-color 0.2s, transform 0.15s;
}
.store:hover { border-color: #d48c1c; }
.store:active { transform: scale(0.98); }
.store:focus-visible { outline: 2px solid #d48c1c; outline-offset: 2px; }
.store span { display: flex; flex-direction: column; line-height: 1.1; text-align: left; }
.store small { font-size: 10px; color: rgba(245,240,232,0.7); }
.store b { font-size: 16px; font-weight: 600; letter-spacing: -0.2px; }

/* ─── Form panel ────────────────────────────────────── */
.mz-form-panel { display: flex; align-items: center; justify-content: center; padding: 56px 48px; min-height: 100vh; }
.form-panel-inner { max-width: 380px; width: 100%; }
.mobile-brand { display: none; align-items: center; gap: 10px; margin-bottom: 28px; color: #f5f0e8; font-size: 19px; font-weight: 500; }
.form-header { margin-bottom: 28px; }
.form-title {
  font-family: 'Iowan Old Style', 'Palatino Linotype', Palatino, Georgia, serif;
  font-size: 30px; font-weight: 600; color: #f5f0e8; margin: 0 0 6px; letter-spacing: -0.4px;
}
.form-sub { font-size: 14px; color: rgba(245,240,232,0.5); margin: 0; }
.share-note {
  display: flex; align-items: center; gap: 10px; padding: 11px 14px; margin-bottom: 22px; border-radius: 10px;
  background: rgba(212,140,28,0.09); border: 0.5px solid rgba(212,140,28,0.3); color: rgba(245,240,232,0.8); font-size: 13px; line-height: 1.4;
}
.field-group { margin-bottom: 20px; }
.field-label { display: block; font-size: 12px; font-weight: 500; color: rgba(245,240,232,0.65); margin-bottom: 7px; letter-spacing: 0.2px; }

.mobile-promo { display: none; margin-top: 30px; padding-top: 24px; border-top: 0.5px solid rgba(245,240,232,0.08); }
.mobile-formats { display: flex; flex-wrap: wrap; gap: 8px 16px; font-size: 12.5px; color: rgba(245,240,232,0.6); margin-bottom: 16px; }
.mobile-formats .v-icon { margin-right: 2px; }

@media (prefers-reduced-motion: reduce) {
  .eq i, .splash-progress, .dot { animation: none; }
  .eq i { transform: scaleY(0.7); }
}

/* ─── Responsive ────────────────────────────────────── */
@media (max-width: 960px) {
  .mz-layout { grid-template-columns: 1fr; }
  .mz-brand-panel { display: none; }
  .mz-form-panel { padding: 44px 24px; min-height: 100vh; align-items: flex-start; }
  .form-panel-inner { max-width: 440px; margin: 0 auto; }
  .mobile-brand { display: flex; }
  .mobile-promo { display: block; }
}

/* ─── Blog + footer ─────────────────────────────────── */
.mz-blog { border-top: 0.5px solid rgba(245,240,232,0.08); background: #100e13; padding: 64px 24px; }
.mz-blog-in { max-width: 1120px; margin: 0 auto; }
.mz-blog-head { display: flex; align-items: baseline; justify-content: space-between; gap: 16px; margin-bottom: 28px; }
.mz-blog-head h2 { font-family: 'Iowan Old Style', 'Palatino Linotype', Palatino, Georgia, serif; font-size: 30px; font-weight: 600; letter-spacing: -0.5px; color: #f5f0e8; margin: 0; }
.mz-blog-all { color: #d48c1c; text-decoration: none; font-size: 14px; }
.mz-blog-all:hover { text-decoration: underline; }
.mz-blog-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 28px; }
.mz-post { display: block; text-decoration: none; color: inherit; }
.mz-post:focus-visible, .mz-blog-all:focus-visible { outline: 2px solid #d48c1c; outline-offset: 3px; border-radius: 4px; }
.mz-post-img { aspect-ratio: 3 / 2; border-radius: 12px; background-size: cover; background-position: center; margin-bottom: 14px; display: flex; align-items: center; justify-content: center; transition: transform .35s ease; }
.mz-post:hover .mz-post-img { transform: translateY(-3px); }
.mz-post-img span { font-family: 'Iowan Old Style', Palatino, Georgia, serif; font-size: 64px; font-weight: 600; color: rgba(245,240,232,0.22); }
.mz-post-meta { font-size: 12.5px; color: rgba(245,240,232,0.5); margin-bottom: 6px; display: flex; gap: 10px; }
.mz-post-meta b { color: #d48c1c; font-weight: 500; }
.mz-post h3 { font-family: 'Iowan Old Style', Palatino, Georgia, serif; font-size: 20px; line-height: 1.28; font-weight: 600; color: #f5f0e8; margin: 0 0 6px; transition: color .2s; }
.mz-post:hover h3 { color: #d48c1c; }
.mz-post p { font-size: 14px; line-height: 1.6; color: rgba(245,240,232,0.58); margin: 0; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }
.mz-foot { display: flex; flex-wrap: wrap; justify-content: space-between; gap: 10px 24px; padding: 22px 24px; font-size: 13px; color: rgba(245,240,232,0.42); border-top: 0.5px solid rgba(245,240,232,0.08); }
.mz-foot-links { display: flex; flex-wrap: wrap; gap: 6px 20px; }
.mz-foot-links a { color: rgba(245,240,232,0.55); text-decoration: none; }
.mz-foot-links a:hover { color: #d48c1c; }
@media (max-width: 960px) { .mz-blog-grid { grid-template-columns: 1fr; } .mz-blog { padding: 44px 24px; } }
@media (prefers-reduced-motion: reduce) { .mz-post-img { transition: none; } }

/* Vuetify input overrides */
.mz-input >>> .v-input__slot {
  background: rgba(245,240,232,0.05) !important;
  border: 0.5px solid rgba(245,240,232,0.12) !important;
  border-radius: 10px !important;
  transition: border-color 0.2s !important;
}
.mz-input >>> .v-input__slot:hover {
  border-color: rgba(212,140,28,0.35) !important;
}
.mz-input.v-input--is-focused >>> .v-input__slot {
  border-color: #d48c1c !important;
}
.mz-input >>> input {
  color: #f5f0e8 !important;
  font-size: 14px !important;
  caret-color: #d48c1c !important;
}
.mz-input >>> input::placeholder {
  color: rgba(245,240,232,0.2) !important;
}
.mz-input >>> .v-icon {
  color: rgba(245,240,232,0.3) !important;
}
.mz-input.v-input--is-focused >>> .v-icon {
  color: #d48c1c !important;
}
.mz-input >>> .v-messages__message {
  color: #e07070 !important;
  font-size: 12px !important;
}
.mz-input >>> fieldset {
  border: none !important;
}

/* Options row */
.options-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;
  margin-top: -4px;
}
.mz-checkbox >>> .v-label {
  color: rgba(245,240,232,0.55) !important;
  font-size: 13px !important;
}
.checkbox-label {
  color: rgba(245,240,232,0.55);
  font-size: 13px;
}
.forgot-link {
  background: none;
  border: none;
  color: #d48c1c;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  padding: 0;
  font-family: inherit;
  transition: opacity 0.15s;
}
.forgot-link:hover {
  opacity: 0.75;
}

/* Submit button */
.mz-submit {
  width: 100%;
  height: 48px;
  background: #d48c1c;
  border: none;
  border-radius: 10px;
  color: #0c0a0e;
  font-size: 15px;
  font-weight: 500;
  font-family: inherit;
  letter-spacing: 0.2px;
  cursor: pointer;
  transition: opacity 0.2s, transform 0.15s;
  margin-bottom: 16px;
}
.mz-submit:hover:not(.disabled) {
  opacity: 0.88;
}
.mz-submit:active:not(.disabled) {
  transform: scale(0.99);
}
.mz-submit.disabled {
  background: rgba(212,140,28,0.25);
  color: rgba(245,240,232,0.3);
  cursor: not-allowed;
}

/* Loading dots */
.submit-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
}
.dot {
  width: 6px;
  height: 6px;
  background: #0c0a0e;
  border-radius: 50%;
  animation: dotBounce 1.2s ease-in-out infinite;
}
.dot:nth-child(2) { animation-delay: 0.15s; }
.dot:nth-child(3) { animation-delay: 0.3s; }
@keyframes dotBounce {
  0%, 80%, 100% { transform: translateY(0); opacity: 0.6; }
  40%           { transform: translateY(-5px); opacity: 1; }
}

/* Alerts */
.mz-alert {
  display: flex;
  align-items: flex-start;
  gap: 9px;
  padding: 12px 14px;
  border-radius: 9px;
  font-size: 13px;
  line-height: 1.5;
  margin-bottom: 16px;
}
.mz-alert.error {
  background: rgba(192,57,43,0.1);
  border: 0.5px solid rgba(192,57,43,0.25);
  color: #e07070;
}
.mz-alert.success {
  background: rgba(29,158,117,0.1);
  border: 0.5px solid rgba(29,158,117,0.25);
  color: #4ecba0;
}
.alert-in-enter-active, .alert-in-leave-active {
  transition: all 0.25s ease;
}
.alert-in-enter, .alert-in-leave-to {
  opacity: 0;
  transform: translateY(-6px);
}

/* Divider */
.form-divider {
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 24px 0 16px;
  color: rgba(245,240,232,0.2);
  font-size: 12px;
}
.form-divider::before,
.form-divider::after {
  content: '';
  flex: 1;
  height: 0.5px;
  background: rgba(245,240,232,0.08);
}

/* Create-account button */
.mz-register-btn {
  width: 100%;
  height: 46px;
  background: transparent;
  border: 0.5px solid rgba(212,140,28,0.3);
  border-radius: 10px;
  color: #d48c1c;
  font-size: 14px;
  font-weight: 500;
  font-family: inherit;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: background 0.2s, border-color 0.2s;
  margin-bottom: 16px;
}
.mz-register-btn:hover {
  background: rgba(212,140,28,0.07);
  border-color: rgba(212,140,28,0.5);
}
.register-note {
  font-size: 12px;
  color: rgba(245,240,232,0.3);
  line-height: 1.55;
  text-align: center;
}

</style>